/**
 * Contracts for module "admin". Owner: partners.
 * Staff tenant directory, automation suspend, usage limits, AI agent reset, consented impersonation and
 * support diagnostics. Import as `@qm/contracts/admin`.
 */
import { z } from 'zod';

const reason = z.string().trim().min(5).max(500);

export const TenantDirectoryQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'suspended', 'closed']).optional(),
  automation: z.enum(['suspended', 'running']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(200).optional(),
});

export const ReasonBodySchema = z.object({ reason });

export const USAGE_METRICS = ['messages_month', 'seats', 'ai_actions_month'] as const;
export const UsageMetricSchema = z.enum(USAGE_METRICS);
export const UsageLimitsUpdateSchema = z.object({
  limits: z.partialRecord(UsageMetricSchema, z.number().int().min(0).max(10_000_000)),
  reason,
});
export const UsageConsumeSchema = z.object({
  tenantId: z.string().min(1).max(64),
  metric: UsageMetricSchema,
  amount: z.number().int().min(1).max(100_000),
  idempotencyKey: z.string().min(8).max(200),
});
export const PartnerLimitsUpdateSchema = z.object({
  clientWorkspaces: z.number().int().min(0).max(10_000).optional(),
  whiteLabelAddon: z.boolean().optional(),
  reason,
});

export const ImpersonationStartSchema = z.object({ tenantId: z.string().min(1).max(64), reason });
export const IMPERSONATION_MAX_MS = 4 * 60 * 60 * 1000;

export const FAILURE_KINDS = ['message', 'webhook', 'template'] as const;
export const FailureKindSchema = z.enum(FAILURE_KINDS);
export const FailureIngestSchema = z.object({
  tenantId: z.string().min(1).max(64),
  kind: FailureKindSchema,
  sourceRef: z.string().min(1).max(200),
  phone: z.string().regex(/^\+?[0-9]{6,20}$/).optional(),
  occurredAt: z.string().datetime({ offset: true }),
  errorCode: z.string().min(1).max(80),
  errorDetail: z.string().max(500).optional(),
  retryable: z.boolean().default(false),
}).strict();
export const FailureSearchSchema = z.object({
  tenantId: z.string().min(1).max(64),
  kind: FailureKindSchema.optional(),
  phone: z.string().regex(/^\+?[0-9]{6,20}$/).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Operator target: locate a failed event in <= 2 minutes (NFR C12-05). The API budget is far below it. */
export const DIAGNOSTICS_SEARCH_BUDGET_MS = 500;

export const ADMIN_EVENTS = {
  automationSuspended: 'admin.automation.suspended',
  automationResumed: 'admin.automation.resumed',
  agentReset: 'admin.agent.reset',
  usageLimitChanged: 'admin.usage_limit.changed',
  failureRetry: 'admin.failure.retry',
  partnerSummaryViewed: 'admin.partner_summary.viewed',
} as const;

/** Normalise a phone number for hashing: digits only, leading + dropped. */
export const normalisePhone = (p: string) => p.replace(/[^0-9]/g, '');

// ---------------------------------------------------------------- AI agent reset (QM-C05-004)
export const AgentConfigRegisterSchema = z.object({
  tenantId: z.string().min(1).max(64),
  agentKey: z.string().trim().min(2).max(80).regex(/^[a-z0-9_.-]+$/),
  version: z.number().int().min(1),
  config: z.record(z.string(), z.unknown()),
  status: z.enum(['approved', 'draft', 'rejected']),
  approvedBy: z.string().max(64).optional(),
  active: z.boolean().default(false),
});
export const ADMIN_AGENT_RESET_EVENT = 'admin.agent.reset';

// ---------------------------------------------------------------- impersonation views (QM-C05-002)
export const IMPERSONATION_RESOURCES = ['summary', 'team', 'usage', 'failures'] as const;
export const ImpersonationResourceSchema = z.enum(IMPERSONATION_RESOURCES);

export const RetrySchema = z.object({ tenantId: z.string().min(1).max(64), reason });

// ---------------------------------------------------------------- partner client privacy (pending item 22)
/**
 * Staff never see a partner-owned client workspace (a tenant an agency created) by name, contact, users or
 * workspace data. They get a stored random alias (`pcl_` + 32 hex), the owning partner, and billing only
 * (plan + MRR + account status). The raw tenant id of such a workspace is not addressable on staff routes.
 */
export const CLIENT_ALIAS_PREFIX = 'pcl_';
export const ClientAliasSchema = z.string().regex(/^pcl_[0-9a-f]{32}$/);
export const isClientAlias = (id: string) => ClientAliasSchema.safeParse(id).success;

export const TenantOwnershipSchema = z.enum(['direct', 'partner_client']);
const money = z.object({ amountMinor: z.number().int(), currency: z.string() }).nullable();

/** Directory row for a partner-owned client workspace: opaque id + partner + billing, nothing else. */
export const MaskedTenantRowSchema = z.object({
  ownership: z.literal('partner_client'),
  id: ClientAliasSchema,
  opaqueId: ClientAliasSchema,
  name: z.null(),
  partner: z.object({ id: z.string(), name: z.string().nullable() }),
  status: z.string(),
  plan: z.string().nullable(),
  mrr: money,
});
export type MaskedTenantRow = z.infer<typeof MaskedTenantRowSchema>;

export const PartnerClientSummarySchema = z.object({
  partner: z.object({ id: z.string(), name: z.string().nullable(), status: z.string().nullable() }),
  clientCount: z.number().int(),
  plans: z.array(z.object({ plan: z.string().nullable(), count: z.number().int() })),
  mrrTotals: z.array(z.object({ currency: z.string(), amountMinor: z.number().int() })),
});
export type PartnerClientSummary = z.infer<typeof PartnerClientSummarySchema>;
